import { ICellRendererAngularComp } from "@ag-grid-community/angular";
import { ICellRendererParams } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { InventoryRule, Marketplace } from "@front/m19-api-client";
import { InventoryRules, InventoryStats } from "@front/m19-models";
import { AsinService } from "@front/m19-services";
import { IButtonDirective, IIconComponent, ITooltipDirective, ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinInelligilibleForInventoryRulePipe } from "@m19-board/inventory/inventory-rules/inventory-rules.pipe";
import { switchMap, tap } from "rxjs";
import {
  AsinInventoryRuleModalComponent,
  AsinInventoryRulesModalData,
  AsinInventoryRulesOutput,
} from "../inventory-rules/asin-inventory-rules-modal.component";

@Component({
  selector: "app-pause-adv-renderer",
  templateUrl: "./pause-adv-renderer.component.html",
  imports: [
    TranslocoDirective,
    IButtonDirective,
    ITooltipDirective,
    AsinInelligilibleForInventoryRulePipe,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PauseAdvRendererComponent implements ICellRendererAngularComp {
  private asinService = inject(AsinService);
  private modalService = inject(ModalService);
  private toasterService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  params: any;
  product = signal<InventoryStats | undefined>(undefined);
  inventoryRules = signal<InventoryRules | undefined>(undefined);
  locale: string = "US";
  accountId!: string;
  marketplace!: Marketplace;
  isReadOnly = signal(false);
  label = computed(() => {
    if (!this.product() || !this.inventoryRules()) {
      return this.translocoService.translate("common.none");
    }

    return this.inventoryRules()!.asinInventoryRule.get(this.product()!.asin)?.advertisingPauseThreshold == undefined
      ? this.translocoService.translate("common.none")
      : "< " +
          this.inventoryRules()!.asinInventoryRule.get(this.product()!.asin)!.advertisingPauseThreshold +
          this.translocoService.translate("common.days_short") +
          (this.inventoryRules()!.asinInventoryRule.get(this.product()!.asin)!.onlyFba
            ? ` (${this.translocoService.translate("common.fba")})`
            : "");
  });

  agInit(
    params: ICellRendererParams & {
      product: InventoryStats;
      inventoryRules: InventoryRules;
      locale: string;
      accountId: string;
      marketplace: Marketplace;
      isReadOnly: boolean;
    },
  ): void {
    this.product.set(params.product);
    this.inventoryRules.set(params.inventoryRules);
    this.locale = params.locale;
    this.accountId = params.accountId;
    this.marketplace = params.marketplace;
    this.isReadOnly.set(params.isReadOnly);
  }

  refresh(
    params: ICellRendererParams & {
      product: InventoryStats;
      inventoryRules: InventoryRules;
      locale: string;
      accountId: string;
      marketplace: Marketplace;
      isReadOnly: boolean;
    },
  ): boolean {
    this.product.set(params.product);
    this.inventoryRules.set(params.inventoryRules);
    this.locale = params.locale;
    this.accountId = params.accountId;
    this.marketplace = params.marketplace;
    this.isReadOnly.set(params.isReadOnly);
    return true;
  }

  setupRuleOnAsin(product: InventoryStats): void {
    const rule = this.inventoryRules()!.asinInventoryRule.get(product.asin);

    this.modalService
      .openModal<AsinInventoryRulesModalData, AsinInventoryRulesOutput>(AsinInventoryRuleModalComponent, {
        modalTitle: this.translocoService.translate("asin-inventory-rules-modal.rule_title", { asin: product.asin }),
        data: {
          accountId: this.accountId,
          marketplace: this.marketplace,
          asin: product.asin,
          rule: rule,
          warningMessage:
            product.orders30d && product.orders30d < InventoryRules.ORDERS30D_ELLIGIBILITY_THRESHOLD
              ? this.translocoService.translate("inventory-table.pause_threshold_tooltip")
              : undefined,
        },
      })
      .afterClosed()
      .pipe(
        switchMap((newRule) => {
          if (newRule === "delete") {
            return this.deleteAsinInventoryRules(product);
          }
          return this.setAsinInventoryRules(newRule, product);
        }),
      )
      .subscribe();
  }

  private setAsinInventoryRules(newRule: InventoryRule, product: InventoryStats) {
    return this.asinService.setAsinInventoryRules(newRule).pipe(
      tap({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("pause-adv-renderer.advertising_pausing_rule_setup_for_asin_product_asin", [
              product.asin,
            ]),
            this.translocoService.translate("pause-adv-renderer.inventory_rule_updated"),
          );
        },
        error: (error) => {
          this.toasterService.error(
            this.translocoService.translate("pause-adv-renderer.error_setting_rule", [product.asin, error]),
            this.translocoService.translate("pause-adv-renderer.inventory_rule_update_error"),
          );
        },
      }),
    );
  }

  private deleteAsinInventoryRules(product: InventoryStats) {
    return this.asinService.deleteAsinInventoryRules(this.accountId, this.marketplace, product.asin).pipe(
      tap({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate(
              "pause-adv-renderer.advertising_pausing_rule_removed_for_asin_product_asin",
              [product.asin],
            ),
            this.translocoService.translate("pause-adv-renderer.inventory_rule_deleted"),
          );
        },
        error: (error) => {
          this.toasterService.error(
            this.translocoService.translate("pause-adv-renderer.error_deleting_rule", [product.asin, error]),
            this.translocoService.translate("pause-adv-renderer.inventory_rule_deletion_error"),
          );
        },
      }),
    );
  }
}
